import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Save, Loader, User, Users, Link2, Check, Camera, X, Trash2, AlertTriangle, ArrowLeft } from 'lucide-react';
import { ref, set, get, remove } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { onAuthStateChanged, signOut, deleteUser, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { database, auth, storage } from '../../shared/firebase';
import { ALL_PROVINCES } from '../utils/provinces';
import { detectTapsodyUser, preparePrefillFromTapsody } from '../utils/tapsodySSO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const INSTRUMENTS = ['Zang', 'Gitaar', 'Basgitaar', 'Drums', 'Keyboard', 'Piano', 'Saxofoon', 'Trompet', 'Viool', 'Cello', 'DJ', 'Productie', 'Anders'];
const GENRES = ['Rock', 'Pop', 'Metal', 'Jazz', 'Blues', 'Funk', 'Soul', 'R&B', 'Hip-hop', 'Electronic', 'Folk', 'Country', 'Klassiek', 'Punk', 'Indie', 'Reggae'];
const EXPERIENCE_LEVELS = [
  { value: 'beginner', label: 'Beginner (0-2 jaar)' },
  { value: 'intermediate', label: 'Gevorderd (2-5 jaar)' },
  { value: 'advanced', label: 'Ervaren (5-10 jaar)' },
  { value: 'professional', label: 'Professioneel (10+ jaar)' },
];
const GOALS = [
  { value: 'hobby', label: 'Hobby — voor de lol spelen' },
  { value: 'gigging', label: 'Optreden — regelmatig gigs spelen' },
  { value: 'recording', label: 'Opnemen — muziek uitbrengen' },
  { value: 'professional', label: 'Professioneel — er van leven' },
];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default function EditProfile() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [existingProfile, setExistingProfile] = useState(null);
  const [tapsodyData, setTapsodyData] = useState(null);
  const [showTapsodyBanner, setShowTapsodyBanner] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoError, setPhotoError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  const [form, setForm] = useState({
    type: 'musician', displayName: '', city: '', province: '', instruments: [], genres: [],
    experienceLevel: '', goals: '', bio: '', mediaLinks: { youtube: '', soundcloud: '', spotify: '' },
    photoUrl: '', tapsodyLinked: false, tapsodyBandIds: [],
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) { navigate('/inloggen'); return; }
      setUser(currentUser);
      
      const snapshot = await get(ref(database, `vjb_profiles/${currentUser.uid}`));
      if (snapshot.exists()) {
        const data = snapshot.val();
        setExistingProfile(data);
        setForm({
          type: data.type || 'musician', displayName: data.displayName || '', city: data.location?.city || '',
          province: data.location?.province || '', instruments: data.instruments || [], genres: data.genres || [],
          experienceLevel: data.experienceLevel || '', goals: data.goals || '', bio: data.bio || '',
          mediaLinks: { youtube: data.mediaLinks?.youtube || '', soundcloud: data.mediaLinks?.soundcloud || '', spotify: data.mediaLinks?.spotify || '' },
          photoUrl: data.photoUrl || '', tapsodyLinked: data.tapsodyLinked || false, tapsodyBandIds: data.tapsodyBandIds || [],
        });
        if (data.photoUrl) setPhotoPreview(data.photoUrl);
      } else {
        const tapsody = await detectTapsodyUser(currentUser.uid);
        if (tapsody) { setTapsodyData(tapsody); setShowTapsodyBanner(true); }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const importFromTapsody = () => {
    if (!tapsodyData) return;
    const prefill = preparePrefillFromTapsody(tapsodyData);
    setForm(prev => ({ ...prev, ...prefill }));
    setShowTapsodyBanner(false);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError('');
    if (!file.type.startsWith('image/')) { setPhotoError('Selecteer een afbeelding'); return; }
    if (file.size > MAX_FILE_SIZE) { setPhotoError('Max 5MB'); return; }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target?.result);
    reader.readAsDataURL(file);
  };

  const clearExistingPhoto = () => { setPhotoFile(null); setPhotoPreview(null); setForm(prev => ({ ...prev, photoUrl: '' })); };

  const uploadPhoto = async () => {
    if (!photoFile || !user) return form.photoUrl;
    setUploadingPhoto(true);
    try {
      const ext = photoFile.name.split('.').pop();
      const filename = `vjb_profiles/${user.uid}/photo_${Date.now()}.${ext}`;
      const photoRef = storageRef(storage, filename);
      await uploadBytes(photoRef, photoFile);
      const downloadUrl = await getDownloadURL(photoRef);
      if (existingProfile?.photoUrl) {
        try {
          const oldPath = existingProfile.photoUrl.split('/o/')[1]?.split('?')[0];
          if (oldPath) await deleteObject(storageRef(storage, decodeURIComponent(oldPath)));
        } catch (e) { console.log('Could not delete old photo:', e); }
      }
      setUploadingPhoto(false);
      return downloadUrl;
    } catch (error) {
      console.error('Error uploading photo:', error);
      setPhotoError('Fout bij uploaden');
      setUploadingPhoto(false);
      return form.photoUrl;
    }
  };

  const toggleArrayItem = (field, item) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(item) ? prev[field].filter(i => i !== item) : [...prev[field], item]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    try {
      let photoUrl = form.photoUrl;
      if (photoFile) photoUrl = await uploadPhoto();
      else if (!photoPreview && existingProfile?.photoUrl) {
        try {
          const oldPath = existingProfile.photoUrl.split('/o/')[1]?.split('?')[0];
          if (oldPath) await deleteObject(storageRef(storage, decodeURIComponent(oldPath)));
        } catch (e) { console.log('Could not delete old photo:', e); }
        photoUrl = '';
      }
      
      const profileData = {
        type: form.type, displayName: form.displayName, email: user.email,
        location: { city: form.city, province: form.province },
        instruments: form.instruments, genres: form.genres, experienceLevel: form.experienceLevel,
        goals: form.goals, bio: form.bio, mediaLinks: form.mediaLinks, photoUrl: photoUrl,
        tapsodyLinked: form.tapsodyLinked, tapsodyBandIds: form.tapsodyBandIds,
        status: existingProfile?.status || 'active', updatedAt: new Date().toISOString(),
        createdAt: existingProfile?.createdAt || new Date().toISOString(),
      };
      await set(ref(database, `vjb_profiles/${user.uid}`), profileData);
      navigate('/profiel');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Er ging iets mis bij het opslaan');
    }
    setSaving(false);
  };

  const handleDeleteAccount = async () => {
    if (!user || !deletePassword) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const credential = EmailAuthProvider.credential(user.email, deletePassword);
      await reauthenticateWithCredential(user, credential);
      
      const adsSnap = await get(ref(database, 'vjb_ads'));
      if (adsSnap.exists()) {
        for (const [adId, ad] of Object.entries(adsSnap.val())) {
          if (ad.createdBy === user.uid) await remove(ref(database, `vjb_ads/${adId}`));
        }
      }
      
      const requestsSnap = await get(ref(database, 'vjb_contact_requests'));
      if (requestsSnap.exists()) {
        for (const [reqId, req] of Object.entries(requestsSnap.val())) {
          if (req.fromUid === user.uid || req.toUid === user.uid) {
            await remove(ref(database, `vjb_contact_requests/${reqId}`));
            await remove(ref(database, `vjb_messages/${reqId}`));
          }
        }
      }
      
      await remove(ref(database, `vjb_request_counts/${user.uid}`));
      await remove(ref(database, `vjb_blocks/${user.uid}`));
      await remove(ref(database, `vjb_deleted_conversations/${user.uid}`));
      
      try {
        const userStorageRef = storageRef(storage, `vjb_profiles/${user.uid}`);
        const files = await listAll(userStorageRef);
        for (const file of files.items) await deleteObject(file);
      } catch (e) { console.log('No storage files:', e); }
      
      await remove(ref(database, `vjb_profiles/${user.uid}`));
      await deleteUser(user);
      navigate('/');
    } catch (error) {
      console.error('Delete account error:', error);
      if (error.code === 'auth/wrong-password') setDeleteError('Onjuist wachtwoord');
      else if (error.code === 'auth/too-many-requests') setDeleteError('Te veel pogingen');
      else setDeleteError('Er ging iets mis');
    }
    setDeleting(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader className="animate-spin text-[#8146FF]" size={32} /></div>;
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      <main className="flex-1 max-w-2xl mx-auto px-4 md:px-6 py-8 w-full">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/profiel" className="p-2 -ml-2 text-white/70 hover:text-white transition">
            <ArrowLeft size={24} />
          </Link>
          <h1 className="text-2xl font-bold">Profiel bewerken</h1>
        </div>

        {showTapsodyBanner && tapsodyData && (
          <div className="bg-white/5 border border-[#8146FF]/30 rounded-2xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#8146FF]/20 flex items-center justify-center flex-shrink-0">
                <Link2 size={24} className="text-[#8146FF]" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">Tapsody account gevonden!</h3>
                <p className="text-white/60 text-sm mb-4">We zien dat je {tapsodyData.bandName} gebruikt in Tapsody. Wil je deze gegevens importeren?</p>
                <div className="flex gap-3">
                  <button onClick={importFromTapsody} className="bg-[#8146FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#6a35d9] transition">Importeren</button>
                  <button onClick={() => setShowTapsodyBanner(false)} className="text-white/50 px-4 py-2 text-sm hover:text-white transition">Overslaan</button>
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Photo */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="font-semibold mb-4">Profielfoto</h2>
            <div className="flex items-center gap-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                  {photoPreview ? <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" /> : <User size={40} className="text-white/40" />}
                </div>
                {photoPreview && (
                  <button type="button" onClick={clearExistingPhoto} className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"><X size={14} /></button>
                )}
              </div>
              <div>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoSelect} className="hidden" id="photo-upload" />
                <label htmlFor="photo-upload" className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg cursor-pointer hover:bg-white/20 transition">
                  <Camera size={18} /> {photoPreview ? 'Wijzig' : 'Upload'}
                </label>
                <p className="text-xs text-white/40 mt-2">JPG, PNG. Max 5MB.</p>
                {photoError && <p className="text-xs text-red-400 mt-1">{photoError}</p>}
              </div>
            </div>
          </div>

          {/* Type */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="font-semibold mb-4">Ik ben een...</h2>
            <div className="grid grid-cols-2 gap-4">
              {['musician', 'band'].map(type => (
                <button key={type} type="button" onClick={() => setForm({...form, type})}
                  className={`p-4 rounded-xl border-2 transition flex flex-col items-center gap-2 ${form.type === type ? 'border-[#8146FF] bg-[#8146FF]/10' : 'border-white/10 hover:border-white/20'}`}>
                  {type === 'musician' ? <User size={24} className={form.type === type ? 'text-[#8146FF]' : 'text-white/40'} /> : <Users size={24} className={form.type === type ? 'text-[#8146FF]' : 'text-white/40'} />}
                  <span className={form.type === type ? 'text-[#8146FF]' : 'text-white/60'}>{type === 'musician' ? 'Muzikant' : 'Band'}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Basic info */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="font-semibold mb-4">Basisgegevens</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-white/70 mb-2">{form.type === 'band' ? 'Bandnaam' : 'Naam'} *</label>
                <input type="text" required value={form.displayName} onChange={(e) => setForm({...form, displayName: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#8146FF]" placeholder={form.type === 'band' ? 'Naam van je band' : 'Je naam'} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/70 mb-2">Stad *</label>
                  <input type="text" required value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#8146FF]" placeholder="Amsterdam" />
                </div>
                <div>
                  <label className="block text-sm text-white/70 mb-2">Provincie *</label>
                  <select required value={form.province} onChange={(e) => setForm({...form, province: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#8146FF]">
                    <option value="" className="bg-gray-900">Kies provincie</option>
                    {ALL_PROVINCES.map(p => <option key={p.code} value={p.code} className="bg-gray-900">{p.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Instruments */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="font-semibold mb-4">{form.type === 'band' ? 'Bezetting' : 'Instrumenten'} *</h2>
            <div className="flex flex-wrap gap-2">
              {INSTRUMENTS.map(inst => (
                <button key={inst} type="button" onClick={() => toggleArrayItem('instruments', inst)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition ${form.instruments.includes(inst) ? 'bg-[#8146FF] text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}>{inst}</button>
              ))}
            </div>
          </div>

          {/* Genres */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="font-semibold mb-4">Genres *</h2>
            <div className="flex flex-wrap gap-2">
              {GENRES.map(genre => (
                <button key={genre} type="button" onClick={() => toggleArrayItem('genres', genre)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition ${form.genres.includes(genre) ? 'bg-[#8146FF] text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}>{genre}</button>
              ))}
            </div>
          </div>

          {/* Experience & Goals */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="font-semibold mb-4">Ervaring & doelen</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-white/70 mb-2">Ervaringsniveau</label>
                <select value={form.experienceLevel} onChange={(e) => setForm({...form, experienceLevel: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#8146FF]">
                  <option value="" className="bg-gray-900">Kies niveau</option>
                  {EXPERIENCE_LEVELS.map(l => <option key={l.value} value={l.value} className="bg-gray-900">{l.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-2">Doel</label>
                <select value={form.goals} onChange={(e) => setForm({...form, goals: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#8146FF]">
                  <option value="" className="bg-gray-900">Kies doel</option>
                  {GOALS.map(g => <option key={g.value} value={g.value} className="bg-gray-900">{g.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="font-semibold mb-4">{form.type === 'band' ? 'Over de band' : 'Over mij'}</h2>
            <textarea value={form.bio} onChange={(e) => setForm({...form, bio: e.target.value})} rows={4} maxLength={1000}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#8146FF] resize-none"
              placeholder={form.type === 'band' ? 'Vertel iets over de band...' : 'Vertel iets over jezelf...'} />
            <p className="text-xs text-white/40 mt-1 text-right">{form.bio.length}/1000</p>
          </div>

          {/* Media Links */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h2 className="font-semibold mb-4">Media (optioneel)</h2>
            <div className="space-y-4">
              {['youtube', 'soundcloud', 'spotify'].map(platform => (
                <div key={platform}>
                  <label className="block text-sm text-white/70 mb-2 capitalize">{platform}</label>
                  <input type="url" value={form.mediaLinks[platform]} onChange={(e) => setForm({...form, mediaLinks: {...form.mediaLinks, [platform]: e.target.value}})}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#8146FF]" placeholder={`https://${platform}.com/...`} />
                </div>
              ))}
            </div>
          </div>

          {form.tapsodyLinked && (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center"><Check size={20} className="text-green-400" /></div>
              <div><p className="font-medium">Gekoppeld met Tapsody</p><p className="text-sm text-white/50">Je account is verbonden met Tapsody App</p></div>
            </div>
          )}

          <button type="submit" disabled={saving || uploadingPhoto || !form.displayName || !form.city || !form.province || form.instruments.length === 0 || form.genres.length === 0}
            className="w-full bg-[#8146FF] text-white py-4 rounded-full font-semibold hover:bg-[#6a35d9] transition disabled:opacity-50 flex items-center justify-center gap-2">
            {(saving || uploadingPhoto) ? <Loader className="animate-spin" size={20} /> : <Save size={20} />}
            {uploadingPhoto ? 'Foto uploaden...' : saving ? 'Opslaan...' : 'Profiel opslaan'}
          </button>
        </form>

        {/* Danger zone */}
        <div className="mt-12 pt-6 border-t border-white/10">
          <div className="bg-white/5 border border-red-500/30 rounded-2xl p-6">
            <p className="text-white/60 text-sm mb-4">Het verwijderen van je account is permanent. Al je gegevens worden verwijderd.</p>
            <button type="button" onClick={() => setShowDeleteModal(true)} className="flex items-center gap-2 px-4 py-2 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/10 transition">
              <Trash2 size={16} /> Account verwijderen
            </button>
          </div>
        </div>
      </main>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center"><AlertTriangle size={24} className="text-red-400" /></div>
            <h3 className="text-xl font-bold text-center mb-2">Account verwijderen?</h3>
            <p className="text-white/60 text-center text-sm mb-6">Dit kan niet ongedaan worden.</p>
            <div className="mb-4">
              <label className="block text-sm text-white/70 mb-2">Bevestig met je wachtwoord</label>
              <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500" placeholder="Je wachtwoord" />
            </div>
            {deleteError && <p className="text-red-400 text-sm mb-4">{deleteError}</p>}
            <div className="flex gap-3">
              <button onClick={() => { setShowDeleteModal(false); setDeletePassword(''); setDeleteError(''); }} className="flex-1 py-2 rounded-lg border border-white/20 text-white/70 hover:bg-white/5 transition" disabled={deleting}>Annuleren</button>
              <button onClick={handleDeleteAccount} disabled={deleting || !deletePassword} className="flex-1 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2">
                {deleting ? <Loader className="animate-spin" size={16} /> : <Trash2 size={16} />} {deleting ? 'Verwijderen...' : 'Verwijderen'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
